import React, { useEffect, useState } from 'react';
import apiClient, { formatApiError } from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import * as PhosphorIcons from '@phosphor-icons/react';

const { Plus, Bug, Sparkle, Calendar, ChatCircle, CheckCircle, Clock, HourglassMedium } = PhosphorIcons;

const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL || 'https://calendly.com/your-demo-link';

const TYPE_META = {
  bug: { label: 'Bug Report', Icon: Bug, color: 'text-red-500', bg: 'bg-red-50 border-red-200' },
  feature: { label: 'Feature Request', Icon: Sparkle, color: 'text-blue-500', bg: 'bg-blue-50 border-blue-200' },
  demo: { label: 'Demo Request', Icon: Calendar, color: 'text-purple-500', bg: 'bg-purple-50 border-purple-200' },
  other: { label: 'Other', Icon: ChatCircle, color: 'text-zinc-500', bg: 'bg-zinc-50 border-zinc-200' },
};

const STATUS_META = {
  open: { label: 'Open', Icon: Clock, color: 'text-amber-700 bg-amber-50 border-amber-200' },
  in_progress: { label: 'In Progress', Icon: HourglassMedium, color: 'text-blue-700 bg-blue-50 border-blue-200' },
  resolved: { label: 'Resolved', Icon: CheckCircle, color: 'text-green-700 bg-green-50 border-green-200' },
};

const Feedback = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ type: 'bug', subject: '', message: '' });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const res = await apiClient.get('/feedback');
      setItems(res.data);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiClient.post('/feedback', form);
      toast.success('Feedback submitted. The team will review it.');
      setOpen(false);
      setForm({ type: 'bug', subject: '', message: '' });
      fetchItems();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-[#6b665c] font-mono">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="feedback-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" data-testid="feedback-title">Feedback & Support</h1>
          <p className="text-[#6b665c] font-mono text-sm tracking-wider uppercase">Report bugs, request features, or book a live demo</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="book-demo-btn">
            <Button className="bg-[#e4ebe5] hover:bg-[#d9d3c5] text-[#3f5246] border border-[#c5d4c8]">
              <Calendar size={18} className="mr-2" />Book a Demo
            </Button>
          </a>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="new-feedback-btn">
                <Plus size={18} className="mr-2" />New Feedback
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
              <DialogHeader><DialogTitle className="font-mono">Submit Feedback</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="feedback-form">
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" data-testid="feedback-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
                      <SelectItem value="bug">Bug Report</SelectItem>
                      <SelectItem value="feature">Feature Request</SelectItem>
                      <SelectItem value="demo">Request a Demo</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Subject</Label>
                  <Input
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]"
                    required
                    maxLength={200}
                    placeholder="Short summary"
                    data-testid="feedback-subject-input"
                  />
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Details</Label>
                  <Textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26] min-h-[140px]"
                    required
                    maxLength={4000}
                    placeholder="Steps to reproduce, expected behaviour, or what you'd like to see..."
                    data-testid="feedback-message-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white border-0" disabled={submitting} data-testid="submit-feedback-btn">
                  {submitting ? 'Submitting...' : 'Submit'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-[#c5d4c8] rounded-md text-center py-12" data-testid="feedback-empty">
          <ChatCircle size={48} className="text-[#a39e92] mx-auto mb-4" />
          <p className="text-[#8a8477] font-mono">No feedback yet. Use the buttons above to submit a bug report, request a feature, or book a demo.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((f) => {
            const meta = TYPE_META[f.type] || TYPE_META.other;
            const status = STATUS_META[f.status] || STATUS_META.open;
            return (
              <div
                key={f.id}
                className="bg-white border border-[#c5d4c8] rounded-md p-5 transition-all duration-200 hover:-translate-y-0.5"
                data-testid={`feedback-item-${f.id}`}
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${meta.bg}`}>
                      <meta.Icon size={20} className={meta.color} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-sm mb-0.5 truncate">{f.subject}</h3>
                      <p className="text-xs text-[#8a8477] font-mono">{meta.label} · {new Date(f.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <span className={`shrink-0 px-2 py-0.5 rounded text-xs font-mono border flex items-center gap-1 ${status.color}`}>
                    <status.Icon size={12} />
                    {status.label}
                  </span>
                </div>
                <p className="text-sm text-[#2d2a26] whitespace-pre-wrap">{f.message}</p>
                {f.admin_response && (
                  <div className="mt-4 bg-[#f5f0e8] border-l-2 border-[#7c9082] p-3 rounded">
                    <p className="text-xs text-[#5a6e5f] font-mono uppercase tracking-wider mb-1">Response from team</p>
                    <p className="text-sm text-[#2d2a26] whitespace-pre-wrap">{f.admin_response}</p>
                    {f.resolved_at && <p className="text-xs text-[#8a8477] font-mono mt-2">Resolved {new Date(f.resolved_at).toLocaleString()}</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Feedback;
